import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { mdiCheckboxMarked, mdiDelete } from '@mdi/js';

import { PageDrawer } from 'common/components/PageDrawer';
import { NotificationListItem } from 'common/components/NotificationListItem';
import { Divider } from 'common/components/Divider';
import { BoxLoader } from 'common/components/Loader';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { InfiniteScroll } from 'common/components/InfiniteScroll';
import { useNotificationsList } from 'common/hooks/useNotificationsList';
import { Button } from 'common/components/Button';
import { IconButton } from 'common/components/IconButton';
import {
    CurrentUserDocument,
    DeleteNotificationsDocument,
    SetNotificationReadStatusDocument,
} from 'graphql/types';
import { useUnreadNotificationCount } from 'common/hooks/useUnreadNotificationCount';
import { useApolloError } from 'common/hooks/useApolloError';
import { Tooltip } from 'common/components/Tooltip';

interface IProps {
    open: boolean;
    onClose?(): void;
}

export const NotificationsDrawer = ({ open, onClose }: IProps) => {
    const [translate] = useTranslation();
    const { closeGlobalDrawer } = useGlobalDrawer();
    const [drawerElement, setDrawerElement] = useState<HTMLDivElement | null>(
        null
    );
    const {
        notifications,
        loading: notificationsLoading,
        hasNextPage,
        fetchMoreResults,
    } = useNotificationsList();
    const { showApolloError } = useApolloError();
    const { data } = useQuery(CurrentUserDocument);
    const { updateUnreadNotificationCount } = useUnreadNotificationCount();
    const [deleteNotifications, { loading: deleteNotificationsLoading }] =
        useMutation(DeleteNotificationsDocument);
    const [individualNotificationLoading, setIndividualNotificationLoading] =
        useState(false);

    const unreadNotificationCount =
        data?.currentUser?.unreadNotificationCount || 0;

    const [
        setNotificationReadStatus,
        { loading: setNotificationReadStatusLoading },
    ] = useMutation(SetNotificationReadStatusDocument);

    // Use callback ref to always get the latest drawer element
    // Changes in the scrollElement can occur when new notifications are added through the cache
    const drawerRef = useCallback((node: HTMLDivElement | null) => {
        setDrawerElement(node);
    }, []);

    const handleMarkAllNotificationsAsRead = async () => {
        try {
            await setNotificationReadStatus({
                variables: {
                    selectAll: true,
                },
                update: (cache) => {
                    // Evict the notifications field so they will be refetched with new readOn values
                    cache.evict({ fieldName: 'notifications' });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        // Make sure to set the unread notification count to 0
        updateUnreadNotificationCount(0, true);
    };

    const handleDeleteNotifications = async () => {
        try {
            await deleteNotifications({
                variables: { selectAll: true },
                update: (cache) => {
                    cache.evict({ fieldName: 'notifications' });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        // Make sure to set the unread notification count to 0
        updateUnreadNotificationCount(0, true);
    };

    const loading =
        notificationsLoading ||
        setNotificationReadStatusLoading ||
        deleteNotificationsLoading;

    const disableActionButton = loading || individualNotificationLoading;

    return (
        <PageDrawer
            open={open}
            ref={drawerRef}
            sx={{ maxWidth: '400px', width: '100%' }}
            onClose={onClose}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <Typography variant="h3">
                    {translate('notificationsDrawer.title')}
                </Typography>

                <Box sx={{ ml: 'auto' }}>
                    {!!unreadNotificationCount && !!notifications.length && (
                        <Tooltip
                            title={translate(
                                'notificationsDrawer.markAllAsRead'
                            )}
                        >
                            <Box component="span">
                                <IconButton
                                    disabled={disableActionButton}
                                    iconPath={mdiCheckboxMarked}
                                    iconSize="2.2rem"
                                    onClick={
                                        !!unreadNotificationCount
                                            ? handleMarkAllNotificationsAsRead
                                            : undefined
                                    }
                                />
                            </Box>
                        </Tooltip>
                    )}

                    {!!notifications.length && (
                        <Tooltip
                            title={translate('notificationsDrawer.deleteAll')}
                        >
                            <Box component="span">
                                <IconButton
                                    disabled={disableActionButton}
                                    iconPath={mdiDelete}
                                    iconSize="2.2rem"
                                    onClick={handleDeleteNotifications}
                                />
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <Divider />

            {!loading && !!notifications.length && (
                <InfiniteScroll
                    shouldRunCallback
                    scrollElement={drawerElement || undefined}
                    onScrollBottom={fetchMoreResults}
                >
                    {notifications.map((notification) => (
                        <Box key={notification.id} sx={{ overflow: 'visible' }}>
                            <NotificationListItem
                                disableActions={loading}
                                notification={notification}
                                onClick={() => {
                                    closeGlobalDrawer({
                                        type: IGlobalDrawerType.Notifications,
                                    });
                                }}
                                onLoading={setIndividualNotificationLoading}
                            />

                            <Divider />
                        </Box>
                    ))}
                </InfiniteScroll>
            )}

            {hasNextPage && (
                <Box
                    sx={{
                        px: 2,
                        mt: 4,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        loading={loading}
                        size="small"
                        variant="contained"
                        onClick={fetchMoreResults}
                    >
                        {translate('notificationsDrawer.loadMoreButton')}
                    </Button>
                </Box>
            )}

            {!loading && !notifications.length && (
                <Box sx={{ px: 2, mt: 2 }}>
                    <Typography variant="body1">
                        {translate('notificationsDrawer.noNotifications')}
                    </Typography>
                </Box>
            )}

            {loading && <BoxLoader />}
        </PageDrawer>
    );
};
